import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import Cookies from "js-cookie";

// Async thunk to fetch notifications
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async ({ userId, page = 1, limit = 10, refresh = false }, { getState, signal, rejectWithValue }) => {
        try {
            let token = Cookies.get('token');

            if (!token) {
                console.log('Token Not found in notification module');
            }

            const loadedPages = getState().notifications?.page || 1;
            const response = await axios.get(`${process.env.NEXT_PUBLIC_BASE_URL}/notifications/${userId}`, {
                signal,
                headers: {
                    Authorization: `Bearer ${token}`
                },
                params: {
                    page,
                    limit: refresh ? Math.max(1, loadedPages) * limit : limit
                }
            });

            return refresh ? { ...response.data, totalPages: Math.ceil(response.data.total / limit) } : response.data;
        } catch (error) {
            console.error('Error fetching notifications:', error);
            return rejectWithValue(error.response?.data || { message: "Unable to load notifications" });
        }
    }
);

const notificationSlice = createSlice({
    name: 'notifications',
    initialState: {
        notifications: [],
        unreadCountByType: {},
        totalUnreadCount: 0,
        notificationLoader: false,
        hasFetched: false,
        page: 1,
        totalPages: 0,
        limit: 10,
        currentRequestId: null,
    },
    reducers: {
        addNotification: (state, action) => {
            if (state.notifications.some((item) => item._id === action.payload._id)) return;
            console.log(state, action, "receiveMessage")
            const { type } = action.payload;
            state.notifications.unshift(action.payload);
            console.log("receiveMessage", !state.unreadCountByType[type])
            if (!state.unreadCountByType[type]) {
                state.unreadCountByType[type] = 0;
            }
            state.unreadCountByType[type]++;
            state.totalUnreadCount++;
        },
        markNotificationAsRead: (state, action) => {
            const { notificationId, type } = action.payload;
            let notification = state.notifications.find(notif => notif._id === notificationId);
            if (notification && !notification.isRead) {
                notification.isRead = true;
                state.unreadCountByType[type]--;
                state.totalUnreadCount--;
            }
        },
        removeNotification: (state, action) => {
            const { notificationId, type } = action.payload;
            let notification = state.notifications.find(notif => notif._id === notificationId);
            if (notification && !notification.isRead) {
                notification.isRead = true;
                state.unreadCountByType[type]--;
                state.totalUnreadCount--;
            }
            state.notifications = state.notifications.filter(notif => notif._id !== notificationId);
        },
        markAllNotificationsAsRead: (state) => {
            state.notifications.forEach(notification => {
                if (!notification.isRead) {
                    notification.isRead = true;
                    state.unreadCountByType[notification.type]--;
                    state.totalUnreadCount--;
                }
            });
        },
        clearNotificationState: (state) => {
            state.notifications = [];
            state.unreadCountByType = {};
            state.totalUnreadCount = 0;
            state.notificationLoader = false;
            state.hasFetched = false;
            state.page = 1;
            state.totalPages = 0;
            state.currentRequestId = null;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        markAsReadAndRemoveNotificationsBySenderAndUser: (state, action) => {
            const { senderId, userId, throughCreatedAt } = action.payload;
            const matches = notification => notification.senderId === senderId && notification.userId === userId &&
                (!throughCreatedAt || new Date(notification.createdAt) <= new Date(throughCreatedAt));

            // Mark notifications as read before removing
            state.notifications.forEach(notification => {
                if (matches(notification) && !notification.isRead) {
                    notification.isRead = true;
                    state.unreadCountByType[notification.type]--;
                    state.totalUnreadCount--;
                }
            });

            // Remove notifications related to the senderId and userId
            state.notifications = state.notifications.filter(
                notification => !matches(notification)
            );
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state, action) => {
                state.currentRequestId = action.meta.requestId;
                if (!action.meta.arg.refresh || !state.hasFetched) state.notificationLoader = true;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;
                state.currentRequestId = null;
                state.notificationLoader = false;
                const incoming = action.payload.data || [];
                const readItems = action.meta.arg.refresh ? state.notifications.filter((item) => item.isRead) : [];
                const items = action.meta.arg.refresh || action.meta.arg.page === undefined || action.meta.arg.page === 1
                    ? [...incoming, ...readItems]
                    : [...state.notifications, ...incoming];
                state.notifications = [...new Map(items.map((item) => [item._id, item])).values()]
                    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

                // A response started before a read acknowledgement must not restore its badge.
                const readIds = new Set(readItems.map((item) => item._id));
                const staleUnread = incoming.filter((item) => !item.isRead && readIds.has(item._id)).length;
                state.totalUnreadCount = Math.max(0, (action.payload.total ?? incoming.filter((item) => !item.isRead).length) - staleUnread);
                state.page = action.meta.arg.refresh
                    ? Math.min(state.page, Math.max(1, action.payload.totalPages))
                    : action.payload.page;
                state.totalPages = action.payload.totalPages;

                // Calculate unread count by type
                state.unreadCountByType = {};
                state.notifications.forEach(notification => {
                    if (!notification.isRead) {
                        if (!state.unreadCountByType[notification.type]) {
                            state.unreadCountByType[notification.type] = 0;
                        }
                        state.unreadCountByType[notification.type]++;
                    }
                });
                state.hasFetched = true;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return;
                state.currentRequestId = null;
                state.notificationLoader = false;
            });
    },
});


export const {
    addNotification,
    markAsReadAndRemoveNotificationsBySenderAndUser,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    clearNotificationState,
    setPage,
    removeNotification
} = notificationSlice.actions;

export default notificationSlice.reducer;
